import 'temporal-polyfill/global';

import {
	ClassConstructor,
	instanceToInstance,
	instanceToPlain,
	plainToInstance,
	TransformationType,
	TransformFnParams,
} from 'class-transformer';
import spacetime, { ParsableDate, Spacetime } from 'spacetime';
// eslint-disable-next-line no-restricted-imports
import { isNumber } from '../compare';
// eslint-disable-next-line no-restricted-imports
import { convertToInt, convertToPercentage } from '../convert';
// eslint-disable-next-line no-restricted-imports
import { calendarDate } from '../spacetime';
// eslint-disable-next-line no-restricted-imports
import { parseInstant, parseZoned } from '../temporal';

export const temporalPlainDateTransformer = function (
	params: TransformFnParams,
): Temporal.PlainDate | string {
	if (params.type === TransformationType.PLAIN_TO_CLASS) {
		try {
			return Temporal.PlainDate.from(params.value);
		} catch {
			return null;
		}
	} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
		if (params.value instanceof Temporal.PlainDate) {
			return params.value.toString();
		} else {
			return null;
		}
	} else if (params.type === TransformationType.CLASS_TO_CLASS) {
		return params.value;
	} else return params.value;
};

export const temporalZonedTransformer = function (
	params: TransformFnParams,
): Temporal.ZonedDateTime | string {
	if (params.type === TransformationType.PLAIN_TO_CLASS) {
		return parseZoned(params.value);
	} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
		if (params.value instanceof Temporal.ZonedDateTime) {
			return params.value.toString();
		} else {
			return null;
		}
	} else if (params.type === TransformationType.CLASS_TO_CLASS) {
		return params.value;
	} else return params.value;
};

export const temporalInstantTransformer = function (
	params: TransformFnParams,
): Temporal.Instant | number {
	if (params.type === TransformationType.PLAIN_TO_CLASS) {
		return parseInstant(params.value);
	} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
		if (params.value instanceof Temporal.Instant) {
			return params.value.epochMilliseconds;
		} else {
			return null;
		}
	} else if (params.type === TransformationType.CLASS_TO_CLASS) {
		return params.value;
	} else return params.value;
};

const plainToClassSpacetimeValue = function (value: unknown): Spacetime | null {
	if (!value) {
		return null;
	} else if (isNumber(value)) {
		return spacetime(value * 1000);
	} else {
		return spacetime(value as ParsableDate);
	}
};

export const calendarDateTransformer = function (
	params: TransformFnParams,
): Date | string {
	if (params.type === TransformationType.PLAIN_TO_CLASS) {
		const st = plainToClassSpacetimeValue(params.value);
		if (st == null) return null;
		else return calendarDate(st).startOf('date').add(6, 'hour').toNativeDate();
	} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
		return spacetime(params.value).format('iso-utc');
	} else if (params.type === TransformationType.CLASS_TO_CLASS) {
		return params.value;
	} else return params.value;
};

export const dateTransformer = function (params: TransformFnParams) {
	if (params.type === TransformationType.PLAIN_TO_CLASS) {
		const st = plainToClassSpacetimeValue(params.value);
		if (st == null) return null;
		else return st.toNativeDate();
	} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
		return spacetime(params.value).format('iso-utc');
	} else if (params.type === TransformationType.CLASS_TO_CLASS) {
		return params.value;
	} else return params.value;
};

export const stringTransformer = function (params: TransformFnParams) {
	if (params.type === TransformationType.PLAIN_TO_CLASS) {
		if (params.value == null) return null;
		return `${params.value}`;
	} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
		return params.value;
	} else if (params.type === TransformationType.CLASS_TO_CLASS) {
		return params.value;
	} else return params.value;
};

export const integerTransformer = function (params: TransformFnParams) {
	if (params.type === TransformationType.PLAIN_TO_CLASS) {
		if (params.value == null) return null;
		if (params.value instanceof Array) {
			const numArray = [];
			for (let i = 0; params.value.length > i; i++) {
				const parsedInt = convertToInt(params.value[i]);
				if (parsedInt == null) {
					throw new Error(
						`integerTransformer. Could not parse int from ${params.value}`,
					);
				} else {
					numArray.push(parsedInt);
				}
			}
			return numArray;
		} else {
			const parsedInt = convertToInt(params.value);
			if (parsedInt == null) {
				throw new Error(
					`integerTransformer. Could not parse int from ${params.value}`,
				);
			} else {
				return parsedInt;
			}
		}
	} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
		return params.value;
	} else if (params.type === TransformationType.CLASS_TO_CLASS) {
		return params.value;
	} else return params.value;
};

export const floatTransformer = function (params: TransformFnParams) {
	if (params.type === TransformationType.PLAIN_TO_CLASS) {
		if (params.value == null) return null;
		if (params.value instanceof Array) {
			const numArray = [];
			for (let i = 0; params.value.length > i; i++) {
				const parsedFloat = parseFloat(params.value[i]);
				if (parsedFloat == null) {
					throw new Error(
						`floatTransformer. Could not parse float from ${params.value}`,
					);
				} else {
					numArray.push(parsedFloat);
				}
			}
			return numArray;
		} else {
			const parsedFloat = parseFloat(params.value);
			if (parsedFloat == null) {
				throw new Error(
					`floatTransformer. Could not parse float from ${params.value}`,
				);
			} else {
				return parsedFloat;
			}
		}
	} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
		return params.value;
	} else if (params.type === TransformationType.CLASS_TO_CLASS) {
		return params.value;
	} else return params.value;
};

export const percentageTransformer = function (params: TransformFnParams) {
	if (params.type === TransformationType.PLAIN_TO_CLASS) {
		if (params.value == null) return null;
		const parsedInt = convertToPercentage(params.value);
		if (parsedInt == null) {
			throw new Error(
				`percentageTransformer. Could not get percentage from ${params.value}`,
			);
		} else {
			return parsedInt;
		}
	} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
		return params.value;
	} else if (params.type === TransformationType.CLASS_TO_CLASS) {
		return params.value;
	} else return params.value;
};

export const primitive_ClassTransform_Types = {
	string: () => {
		return String;
	},
	number: () => {
		return Number;
	},
};

export const recordTransformerFactory = function <T>(
	genericType: () => ClassConstructor<T>,
) {
	return (params: TransformFnParams) => {
		if (params.type === TransformationType.PLAIN_TO_CLASS) {
			if (params.value != null) {
				const record = {};
				Object.entries(params.value).forEach(([k, v]) => {
					record[k] = plainToInstance(genericType(), v);
				});

				return record;
			} else {
				return null;
			}
		} else if (params.type === TransformationType.CLASS_TO_PLAIN) {
			return instanceToPlain(params.value);
		} else if (params.type === TransformationType.CLASS_TO_CLASS) {
			if (params.value != null) {
				const record = {};
				Object.entries(params.value).forEach(([k, v]) => {
					record[k] = instanceToInstance(genericType(), v);
				});

				return record;
			} else {
				return null;
			}
		} else {
			return params.value;
		}
	};
};
